import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AprimaNowProviderSettingsDto,
  CommandResult,
  CreatePatientDashboardConfigurationV2Command,
  CurrentUserSettingsDto,
  Guid,
  HpTabsSettings,
  KdbConfigurationSettingDto,
  PatientDashboardConfigurationV1,
  PatientDashboardConfigurationV2Dto,
  PrintSetDto,
  ProviderDashboardSettingsDto,
  ProviderEpcsSettingsDto,
  ProviderSettingsDto,
  SigWriterSettingsDto,
  SiteSettingsDto,
  TileDescriptorV2Dto,
  UpdatePatientDashboardConfigurationV2Command,
  UserSettingsDtoLimited,
  UserSettingsMap,
} from 'webcommon/shared';
import { PrmHttpClientService } from '../../PrmHttpClientService';

@Injectable({
  providedIn: 'root',
})
export class SettingRepository {
  static readonly ajsFactoryName = 'SettingRepositoryTs';

  constructor(
    private readonly httpClientService: PrmHttpClientService,
  ) { }

  getKdbConfiguration(userId?: string, renderingProviderId?: string, billingProviderId?: string): Observable<KdbConfigurationSettingDto> {
    let httpParams = new HttpParams();
    if (userId) {
      httpParams = httpParams.set('userId', userId);
    }
    if (renderingProviderId) {
      httpParams = httpParams.set('renderingProviderId', renderingProviderId);
    }
    if (billingProviderId) {
      httpParams = httpParams.set('billingProviderId', billingProviderId);
    }

    const endpointUrl = `settings/kdbconfiguration`;
    return this.httpClientService.sendGetRequest(endpointUrl, httpParams);
  }

  getKdbConfigurationByVisit(userId?: string, visitId?: string): Observable<KdbConfigurationSettingDto> {
    let httpParams = new HttpParams();
    if (userId) {
      httpParams = httpParams.set('userId', userId);
    }
    if (visitId) {
      httpParams = httpParams.set('visitId', visitId);
    }

    const endpointUrl = `settings/kdbconfiguration-visit`;
    return this.httpClientService.sendGetRequest(endpointUrl, httpParams);
  }

  getPatientDashboardTiles(): Observable<TileDescriptorV2Dto[]> {
    const endpointUrl = `settings/patient-dashboard-tiles`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  getPatientDashboardStructureV1(userId: string): Observable<PatientDashboardConfigurationV1[]> {
    const endpointUrl = `settings/patientdashboards/${userId}`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  createPatientDashboardConfiguration(
    dashboardConfiguration: CreatePatientDashboardConfigurationV2Command
  ): Observable<PatientDashboardConfigurationV2Dto> {

    const endpointUrl = `settings/patient-dashboards`;
    return this.httpClientService.sendPostRequest(endpointUrl, dashboardConfiguration);
  }

  deletePatientDashboardConfiguration(
    dashboardConfigurationId: string
  ): Observable<CommandResult> {

    const endpointUrl = `settings/patient-dashboards/${dashboardConfigurationId}`;
    return this.httpClientService.sendDeleteRequest(endpointUrl);
  }

  getPatientDashboardStructureV2(): Observable<PatientDashboardConfigurationV2Dto[]> {
    const endpointUrl = `settings/patient-dashboards`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  updatePatientDashboardConfiguration(
    dashboardConfiguration: UpdatePatientDashboardConfigurationV2Command
  ): Observable<PatientDashboardConfigurationV2Dto> {

    const endpointUrl = `settings/patient-dashboards/${dashboardConfiguration.Id}`;
    return this.httpClientService.sendPutRequest(endpointUrl, dashboardConfiguration);
  }

  getProviderDashboard(userid: string): Observable<ProviderDashboardSettingsDto> {
    const endpointUrl = `settings/providerdashboard/${userid}`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  getProviderEpcsSettings(userId: string, siteId?: string): Observable<ProviderEpcsSettingsDto> {
    const endpointUrl = `settings/providersettings/${userId}/epcs/${(siteId || Guid.empty())}`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  getProviderSettings(userId: string): Observable<ProviderSettingsDto> {
    const endpointUrl = `settings/providersettings/${userId}`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  updateProviderSettings(userId: string, settings: AprimaNowProviderSettingsDto): Observable<ProviderSettingsDto> {
    const endpointUrl = `settings/providersettings/${userId}`;
    return this.httpClientService.sendPutRequest(endpointUrl, settings);
  }

  getSigWriterSettings(): Observable<SigWriterSettingsDto> {
    const endpointUrl = `settings/sigwriter-settings`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  getSiteSettings(): Observable<SiteSettingsDto> {
    const endpointUrl = `settings/site-settings`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  updatePrintSetUserSetting(printSetDto: PrintSetDto): Observable<PrintSetDto> {
    const endpointUrl = `settings/user-settings/print-set`;
    return this.httpClientService.sendPutRequest(endpointUrl, printSetDto);
  }

  getHomepageTabsSettings(): Observable<HpTabsSettings> {
    const endpointUrl = 'settings/homepage-tabs';
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  getSpecifiedUserSettings<T extends Array<keyof UserSettingsMap>>(
    settingIds: [...T],
    ignoreCache: boolean = false,
    loginInitialLoad: boolean = false
  ): Observable<UserSettingsDtoLimited<T[number]>> {
    const params = new HttpParams().set('ignoreCache', String(ignoreCache))
                                    .set('loginInitialLoad', String(loginInitialLoad));
    const endpointUrl = `settings/user-settings`;
    return this.httpClientService.sendPostRequest(endpointUrl, settingIds, params);
  }

  getUserSettings(userid: string): Observable<CurrentUserSettingsDto> {
    const endpointUrl = `settings/usersettings/${userid}`;
    return this.httpClientService.sendGetRequest(endpointUrl);
  }

  getDrugSearchIncludeInactive(providerId: string): Observable<boolean> {
    const params = new HttpParams().set('providerId', String(providerId));
    const endpointUrl = `settings/drug-search-include-inactive`;
    return this.httpClientService.sendGetRequest(endpointUrl, params);
  }
}
